/**
 * PrivacySettings Component
 *
 * Allows users to manage privacy settings like profile visibility,
 * contact information display, and booking preferences.
 *
 * Epic: MVP 1.21.5 - Dashboard Completion
 * Story: SCRUM-528 - Guide Settings Backend (5 SP)
 * Location: src/components/account/PrivacySettings.jsx
 */

import React, { useEffect, useState } from 'react';
import { Shield, Eye, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import apiClient from '../../utils/apiClient';

export function PrivacySettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/guides/settings/privacy');

      if (response.data.success) {
        setSettings(response.data.data || {});
      }
    } catch (err) {
      console.error('Error fetching privacy settings:', err);
      setError(err.message || 'Failed to load privacy settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (field) => {
    setSettings({
      ...settings,
      [field]: !settings[field]
    });
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(false);

      const response = await apiClient.put('/guides/settings/privacy', settings);

      if (response.data.success) {
        setSettings(response.data.data);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error saving privacy settings:', err);
      setError(err.message || 'Failed to save privacy settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-md)',
        border: '1px solid var(--color-border-light)'
      }}>
        <h3 style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-4)'
        }}>
          Privacy Settings
        </h3>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary)' }} />
        </div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div style={{
        backgroundColor: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-md)',
        border: '1px solid var(--color-border-light)'
      }}>
        <h3 style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-4)'
        }}>
          Privacy Settings
        </h3>
        <div style={{
          padding: 'var(--space-4)',
          backgroundColor: 'var(--color-error-light)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-error-dark)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)'
        }}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const privacyOptions = [
    {
      field: 'profile_visibility',
      label: 'Public Profile',
      description: 'Make your profile visible to all users',
      icon: Eye
    },
    {
      field: 'show_location_on_profile',
      label: 'Show Location',
      description: 'Display your location on your public profile',
      icon: Shield
    },
    {
      field: 'show_phone_on_profile',
      label: 'Show Phone Number',
      description: 'Display your phone number on your profile',
      icon: Shield
    },
    {
      field: 'show_email_on_profile',
      label: 'Show Email Address',
      description: 'Display your email address on your profile',
      icon: Shield
    },
    {
      field: 'allow_direct_booking',
      label: 'Allow Direct Booking',
      description: 'Let customers book directly without approval',
      icon: Shield
    },
    {
      field: 'allow_messages_from_non_customers',
      label: 'Allow Messages from Anyone',
      description: 'Receive messages from users who haven\'t booked with you',
      icon: Shield
    }
  ];

  return (
    <div style={{
      backgroundColor: 'var(--color-white)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-6)',
      boxShadow: 'var(--shadow-md)',
      border: '1px solid var(--color-border-light)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <h3 style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-1)',
          margin: 0
        }}>
          Privacy Settings
        </h3>
        <p style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          margin: 0
        }}>
          Control what information is visible on your profile
        </p>
      </div>

      {/* Success Message */}
      {saveSuccess && (
        <div style={{
          padding: 'var(--space-3)',
          backgroundColor: 'var(--color-success-light)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-success-dark)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
          fontSize: 'var(--font-size-sm)'
        }}>
          <CheckCircle2 size={18} />
          <span>Privacy settings saved successfully</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          padding: 'var(--space-3)',
          backgroundColor: 'var(--color-error-light)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-error-dark)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
          fontSize: 'var(--font-size-sm)'
        }}>
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Privacy Options */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
        {privacyOptions.map((option) => {
          const Icon = option.icon;
          const isEnabled = settings?.[option.field];

          return (
            <div
              key={option.field}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border-light)',
                backgroundColor: 'var(--color-gray-50)',
                transition: 'all var(--transition-fast)'
              }}
              onMouseOver={(  _e) => {
                _e.currentTarget.style.backgroundColor = 'var(--color-gray-100)';
              }}
              onMouseOut={(  _e) => {
                _e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
              }}
            >
              {/* Icon & Info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flex: 1 }}>
                <div style={{
                  minWidth: '40px',
                  height: '40px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: isEnabled ? 'var(--color-success-light)' : 'var(--color-gray-200)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isEnabled ? 'var(--color-success)' : 'var(--color-text-tertiary)'
                }}>
                  <Icon size={20} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--color-text-primary)',
                    marginBottom: '2px'
                  }}>
                    {option.label}
                  </div>
                  <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)'
                  }}>
                    {option.description}
                  </div>
                </div>
              </div>

              {/* Toggle Switch */}
              <button
                onClick={() => handleToggle(option.field)}
                style={{
                  position: 'relative',
                  width: '48px',
                  height: '24px',
                  borderRadius: 'var(--radius-full)',
                  backgroundColor: isEnabled ? 'var(--color-success)' : 'var(--color-gray-300)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)',
                  padding: 0
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: isEnabled ? '26px' : '2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: 'var(--radius-full)',
                  backgroundColor: 'var(--color-white)',
                  transition: 'all var(--transition-fast)',
                  boxShadow: 'var(--shadow-sm)'
                }} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          width: '100%',
          padding: 'var(--space-3)',
          backgroundColor: saving ? 'var(--color-gray-300)' : 'var(--color-primary)',
          color: 'var(--color-white)',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          cursor: saving ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          transition: 'all var(--transition-fast)'
        }}
        onMouseOver={(  _e) => {
          if (!saving) {
            _e.currentTarget.style.backgroundColor = 'var(--color-primary-dark)';
          }
        }}
        onMouseOut={(  _e) => {
          if (!saving) {
            _e.currentTarget.style.backgroundColor = 'var(--color-primary)';
          }
        }}
      >
        {saving ? (
          <>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Saving...
          </>
        ) : (
          <>
            <Shield size={18} />
            Save Privacy Settings
          </>
        )}
      </button>
    </div>
  );
}

export default PrivacySettings;
